import React, { useCallback, useRef, useState } from 'react';
import { InferenceError, runInference } from './api';
import { readPerformance, readProcessingId } from './performance';
import ResultImage from './ResultImage';

// One completed request. The timing fields mirror metadata.performance (see
// ./performance for the tree they are flattened from) and are null when the backend
// reported no usable block.
type LogEntry = {
  seq: number;
  name: string;
  status: number | 'ERR';
  ok: boolean;
  latencyMs: number; // round trip measured here, in the browser
  totalMs: number | null; // performance.total_ms — the server's own view
  queueWaitMs: number | null;
  inferenceMs: number | null; // model_inference.total_ms
  publishMs: number | null; // model_inference.publish_ms
  drainMs: number | null; // model_inference.drain_ms
  modelOtherMs: number | null; // model_inference.other_ms
  otherMs: number | null; // performance.other_ms
  processingId: string | null; // metadata.processing_id
  results: unknown; // full parsed response (null on error)
  imageName: string | null; // name the annotated image was stored under; null when storage is off
  error: string | null; // error message when the request failed
};

// Only the headline durations are averaged. The sub-timings stay per-request in the
// table, which is where that level of detail belongs.
type Stats = {
  dispatched: number;
  completed: number;
  ok: number;
  busy: number; // 503 — pool exhausted
  failed: number;
  latencySumMs: number; // running sum of round-trip latencies (all completed)
  totalSumMs: number; // running sum of server total_ms
  inferenceSumMs: number; // running sum of server model_inference.total_ms
  inferenceCount: number; // responses that reported a performance block (divisor for both server averages)
};

const EMPTY_STATS: Stats = {
  dispatched: 0,
  completed: 0,
  ok: 0,
  busy: 0,
  failed: 0,
  latencySumMs: 0,
  totalSumMs: 0,
  inferenceSumMs: 0,
  inferenceCount: 0,
};
const MAX_LOG_ROWS = 200;

// Every server-reported timing on a request that never produced a response.
const EMPTY_ENTRY_TIMINGS = {
  totalMs: null,
  queueWaitMs: null,
  inferenceMs: null,
  publishMs: null,
  drainMs: null,
  modelOtherMs: null,
  otherMs: null,
} satisfies Partial<LogEntry>;

// Camera-frame-rate presets for the send interval (fps -> ms between sends).
const FPS_PRESETS = [30, 60];
const fpsToMs = (fps: number) => Math.round(1000 / fps);

// Lightweight per-request row kept for the (uncapped) markdown export.
type ExportRow = Pick<
  LogEntry,
  | 'seq'
  | 'name'
  | 'status'
  | 'latencyMs'
  | 'totalMs'
  | 'queueWaitMs'
  | 'inferenceMs'
  | 'publishMs'
  | 'drainMs'
  | 'modelOtherMs'
  | 'otherMs'
  | 'processingId'
>;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Large figures read better whole; the sub-millisecond ones need the decimals.
const WHOLE_MS = 0;
const FINE_MS = 2;
const ms = (value: number | null, digits: number) => (value != null ? value.toFixed(digits) : '—');
const msUnit = (value: number | null, digits: number) =>
  value != null ? `${value.toFixed(digits)} ms` : '—';

// Build a shareable markdown report: a summary table on top, then the full
// (uncapped) per-request details table.
const buildMarkdown = (data: {
  rows: ExportRow[];
  dispatched: number;
  total: number;
  completed: number;
  ok: number;
  busy: number;
  failed: number;
  throughput: string;
  avgLatency: string;
  avgServer: string;
  avgInference: string;
  elapsedMs: number;
  intervalMs: number;
}): string => {
  const summary = [
    '## Summary',
    '',
    '| Metric | Value |',
    '| --- | --- |',
    `| Dispatched | ${data.dispatched} / ${data.total} |`,
    `| Completed | ${data.completed} |`,
    `| OK | ${data.ok} |`,
    `| Busy (503) | ${data.busy} |`,
    `| Failed | ${data.failed} |`,
    `| Throughput | ${data.throughput}/s |`,
    `| Avg latency | ${data.avgLatency} |`,
    `| Avg server | ${data.avgServer} |`,
    `| Avg inference | ${data.avgInference} |`,
    `| Duration | ${(data.elapsedMs / 1000).toFixed(1)} s |`,
    `| Interval | ${data.intervalMs} ms |`,
  ];

  // Markdown has no grouped header, so the four columns that live inside
  // model_inference are named for their parent rather than relying on position.
  const details = [
    '## Details',
    '',
    '| # | File | Process ID | Status | Latency (ms) | Total (ms) | Queue (ms) | Other (ms) |'
      + ' Model total (ms) | Model publish (ms) | Model drain (ms) | Model other (ms) |',
    `|${' --- |'.repeat(12)}`,
    ...[...data.rows].sort((a, b) => a.seq - b.seq).map(
      (row) =>
        `| ${row.seq} | ${row.name} | ${row.processingId ?? '—'} | ${row.status} | `
        + `${row.latencyMs.toFixed(WHOLE_MS)} | ${ms(row.totalMs, WHOLE_MS)} | `
        + `${ms(row.queueWaitMs, FINE_MS)} | ${ms(row.otherMs, FINE_MS)} | `
        + `${ms(row.inferenceMs, WHOLE_MS)} | ${ms(row.publishMs, FINE_MS)} | `
        + `${ms(row.drainMs, FINE_MS)} | ${ms(row.modelOtherMs, FINE_MS)} |`,
    ),
  ];

  return [
    '# Load simulation results',
    '',
    `Generated: ${new Date().toISOString()}`,
    '',
    ...summary,
    '',
    ...details,
    '',
  ].join('\n');
};

/**
 * Load-simulation panel. Dispatches (files × repeat) inference requests spaced
 * by a fixed interval, firing each on the timer without waiting for the previous
 * response so the backend's pipeline concurrency is actually exercised.
 */
function TestPanel() {
  const [files, setFiles] = useState<File[]>([]);
  const [repeat, setRepeat] = useState(1);
  const [intervalMs, setIntervalMs] = useState(100);
  const [running, setRunning] = useState(false);
  const [stats, setStats] = useState<Stats>(EMPTY_STATS);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [finishedAt, setFinishedAt] = useState<number | null>(null);
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);

  const cancelRef = useRef(false);
  const inFlightRef = useRef(0);
  const exportRowsRef = useRef<ExportRow[]>([]);

  const total = files.length * Math.max(0, repeat);
  const selectedEntry = selectedSeq != null ? log.find((entry) => entry.seq === selectedSeq) ?? null : null;

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiles(Array.from(event.target.files ?? []));
  };

  const record = useCallback((entry: LogEntry) => {
    const { results, imageName, error, ok, ...exportRow } = entry;
    exportRowsRef.current.push(exportRow);
    setLog((prev) => [entry, ...prev].slice(0, MAX_LOG_ROWS));
    setStats((prev) => ({
      ...prev,
      completed: prev.completed + 1,
      ok: prev.ok + (entry.ok ? 1 : 0),
      busy: prev.busy + (entry.status === 503 ? 1 : 0),
      failed: prev.failed + (!entry.ok ? 1 : 0),
      latencySumMs: prev.latencySumMs + entry.latencyMs,
      totalSumMs: prev.totalSumMs + (entry.totalMs ?? 0),
      inferenceSumMs: prev.inferenceSumMs + (entry.inferenceMs ?? 0),
      inferenceCount: prev.inferenceCount + (entry.inferenceMs != null ? 1 : 0),
    }));
  }, []);

  const dispatchOne = useCallback(
    (file: File, seq: number) => {
      inFlightRef.current += 1;
      setStats((prev) => ({ ...prev, dispatched: prev.dispatched + 1 }));
      const start = performance.now();
      runInference(file)
        .then((res) => {
          const perf = readPerformance(res.results);
          record({
            seq,
            name: file.name,
            status: 200,
            ok: true,
            latencyMs: performance.now() - start,
            totalMs: perf.totalMs,
            queueWaitMs: perf.queueWaitMs,
            inferenceMs: perf.modelInferenceMs,
            publishMs: perf.publishMs,
            drainMs: perf.drainMs,
            modelOtherMs: perf.modelOtherMs,
            otherMs: perf.otherMs,
            processingId: readProcessingId(res.results),
            results: res.results,
            imageName: res.imageName,
            error: null,
          });
        })
        .catch((err: unknown) => {
          const status = err instanceof InferenceError ? err.status ?? 'ERR' : 'ERR';
          record({
            seq,
            name: file.name,
            status,
            ok: false,
            latencyMs: performance.now() - start,
            // Nothing came back, so there is no server-side timing to report.
            ...EMPTY_ENTRY_TIMINGS,
            processingId: null,
            results: null,
            imageName: null,
            error: err instanceof Error ? err.message : 'Something went wrong.',
          });
        })
        .finally(() => {
          inFlightRef.current -= 1;
        });
    },
    [record],
  );

  const stop = () => {
    cancelRef.current = true;
  };

  const start = async () => {
    if (running || total === 0) {
      return;
    }
    cancelRef.current = false;
    exportRowsRef.current = [];
    setStats(EMPTY_STATS);
    setLog([]);
    setSelectedSeq(null);
    setFinishedAt(null);
    setStartedAt(Date.now());
    setRunning(true);

    let seq = 0;
    for (let pass = 0; pass < repeat && !cancelRef.current; pass += 1) {
      for (const file of files) {
        if (cancelRef.current) {
          break;
        }
        seq += 1;
        dispatchOne(file, seq);
        if (seq < total) {
          await sleep(intervalMs);
        }
      }
    }

    // Let the still-in-flight responses settle before declaring the run over.
    while (inFlightRef.current > 0) {
      await sleep(50);
    }
    setFinishedAt(Date.now());
    setRunning(false);
  };

  const elapsedMs = startedAt != null ? (finishedAt ?? Date.now()) - startedAt : 0;
  const throughput = elapsedMs > 0 ? (stats.completed / (elapsedMs / 1000)).toFixed(1) : '0.0';
  const avgLatency =
    stats.completed > 0 ? `${(stats.latencySumMs / stats.completed).toFixed(0)} ms` : '—';
  // Both server averages share the same divisor: the responses that actually reported a
  // timing block. Widest to narrowest, they nest — latency ⊇ server ⊇ inference.
  const avgServerPerf = (sum: number) =>
    stats.inferenceCount > 0 ? `${(sum / stats.inferenceCount).toFixed(WHOLE_MS)} ms` : '—';
  const avgServer = avgServerPerf(stats.totalSumMs);
  const avgInference = avgServerPerf(stats.inferenceSumMs);

  const handleExport = () => {
    const md = buildMarkdown({
      rows: exportRowsRef.current,
      dispatched: stats.dispatched,
      total,
      completed: stats.completed,
      ok: stats.ok,
      busy: stats.busy,
      failed: stats.failed,
      throughput,
      avgLatency,
      avgServer,
      avgInference,
      elapsedMs,
      intervalMs,
    });
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${stamp}_${total}-reqs-in-${intervalMs}ms.md`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <>
    <section className="card test-panel">
      <div className="result-header">
        <h2>Load simulation</h2>
        <p>
          Send {total || 0} request{total === 1 ? '' : 's'} ({files.length} image
          {files.length === 1 ? '' : 's'} × {repeat}) spaced by {intervalMs} ms. Requests fire on the
          timer without waiting for responses, so the backend pipeline pool is exercised concurrently.
        </p>
      </div>

      <div className="test-grid">
        <label className="field test-full">
          <span className="label">Images (one or more)</span>
          <input type="file" accept="image/*" multiple onChange={handleFilesChange} disabled={running} />
        </label>

        <div className="test-row">
          <label className="field">
            <span className="label">Repeat (passes over the images)</span>
            <input
              type="number"
              min={1}
              value={repeat}
              onChange={(e) => setRepeat(Math.max(1, Number(e.target.value) || 1))}
              disabled={running}
            />
          </label>

          <label className="field">
            <span className="label">Interval between sends (ms)</span>
            <div className="interval-row">
              <input
                type="number"
                min={0}
                value={intervalMs}
                onChange={(e) => setIntervalMs(Math.max(0, Number(e.target.value) || 0))}
                disabled={running}
              />
              <div className="fps-presets">
                {FPS_PRESETS.map((fps) => (
                  <button
                    key={fps}
                    type="button"
                    className="ghost"
                    onClick={() => setIntervalMs(fpsToMs(fps))}
                    disabled={running}
                  >
                    {fps} fps
                  </button>
                ))}
              </div>
            </div>
          </label>
        </div>
      </div>

      <div className="test-actions">
        {running ? (
          <button type="button" className="primary" onClick={stop}>
            Stop
          </button>
        ) : (
          <button type="button" className="primary" onClick={start} disabled={total === 0}>
            Send {total || 0} request{total === 1 ? '' : 's'}
          </button>
        )}
      </div>

      <div className="stats-grid">
        <Stat label="Dispatched" value={`${stats.dispatched} / ${total}`} />
        <Stat label="Completed" value={stats.completed} />
        <Stat label="OK" value={stats.ok} tone="ok" />
        <Stat label="Busy (503)" value={stats.busy} tone="warn" />
        <Stat label="Failed" value={stats.failed} tone="err" />
      </div>

      {/* Headline numbers only. The per-timing breakdown lives in the table below,
          where it can be read against the request it belongs to. */}
      <div className="stats-grid">
        <Stat label="Throughput" value={`${throughput}/s`} />
        <Stat label="Avg latency" value={avgLatency} />
        <Stat label="Avg server" value={avgServer} />
        <Stat label="Avg inference" value={avgInference} />
      </div>

      <div className="log-output">
        <div className="log-header">
          <span className="json-header">Request log (newest first, last {MAX_LOG_ROWS})</span>
        </div>
        {log.length > 0 ? (
          <div className="log-scroll">
            <table className="log-table">
              {/* Two header rows: the first does nothing but span the four columns that
                  are measured *inside* model inference, so the table shows the same
                  containment the response body does. Publish, drain and the model's own
                  residual sum to Model total, not alongside it. */}
              <thead>
                <tr className="log-group-row">
                  {/* One filler per ungrouped column, so the label lands over the four
                      it covers. */}
                  {Array.from({ length: 8 }, (_, index) => (
                    <th key={index} aria-hidden="true" />
                  ))}
                  <th
                    className="log-group group-start group-end"
                    colSpan={4}
                    scope="colgroup"
                  >
                    Model inference
                  </th>
                </tr>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">File</th>
                  <th scope="col">Process ID</th>
                  <th scope="col">Status</th>
                  <th scope="col">Latency</th>
                  <th scope="col">Total</th>
                  <th scope="col">Queue</th>
                  <th scope="col">Other</th>
                  <th scope="col" className="group-col group-start">
                    Total
                  </th>
                  <th scope="col" className="group-col">
                    Publish
                  </th>
                  <th scope="col" className="group-col">
                    Drain
                  </th>
                  <th scope="col" className="group-col group-end">
                    Other
                  </th>
                </tr>
              </thead>
              <tbody>
                {log.map((entry) => (
                  <tr
                    key={entry.seq}
                    className={`${entry.ok ? 'row-ok' : 'row-err'} row-selectable${
                      entry.seq === selectedSeq ? ' row-selected' : ''
                    }`}
                    onClick={() => setSelectedSeq(entry.seq)}
                  >
                    <td>{entry.seq}</td>
                    <td>{entry.name}</td>
                    <td title={entry.processingId ?? undefined}>
                      {entry.processingId ? `${entry.processingId.slice(0, 8)}…` : '—'}
                    </td>
                    <td>{entry.status}</td>
                    <td className="latency">{entry.latencyMs.toFixed(WHOLE_MS)} ms</td>
                    <td>{msUnit(entry.totalMs, WHOLE_MS)}</td>
                    <td>{msUnit(entry.queueWaitMs, FINE_MS)}</td>
                    <td>{msUnit(entry.otherMs, FINE_MS)}</td>
                    <td className="group-col group-start">{msUnit(entry.inferenceMs, WHOLE_MS)}</td>
                    <td className="group-col">{msUnit(entry.publishMs, FINE_MS)}</td>
                    <td className="group-col">{msUnit(entry.drainMs, FINE_MS)}</td>
                    <td className="group-col group-end">{msUnit(entry.modelOtherMs, FINE_MS)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="log-empty">No requests yet — run a load test to see results here.</p>
        )}
        <div className="log-footer">
          <button
            type="button"
            className="ghost"
            onClick={handleExport}
            disabled={running || stats.completed === 0}
          >
            Export markdown
          </button>
        </div>
      </div>
    </section>

    {selectedEntry && (
      <section className="card results">
        <div className="result-block">
          <div className="result-header">
            <h2>Request #{selectedEntry.seq} details</h2>
            <p>
              {selectedEntry.name} · status {selectedEntry.status} ·{' '}
              {selectedEntry.latencyMs.toFixed(0)} ms round-trip
              {selectedEntry.inferenceMs != null
                ? ` · ${selectedEntry.inferenceMs.toFixed(0)} ms inference`
                : ''}
            </p>
          </div>

          {selectedEntry.ok ? (
            <>
              <ResultImage
                imageName={selectedEntry.imageName}
                alt={`Inference result for request ${selectedEntry.seq}`}
              />

              <div className="json-output">
                <div className="json-header">Response JSON</div>
                <pre>{JSON.stringify(selectedEntry.results, null, 2)}</pre>
              </div>
            </>
          ) : (
            <p className="message error">{selectedEntry.error ?? 'Request failed.'}</p>
          )}
        </div>
      </section>
    )}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'ok' | 'warn' | 'err' }) {
  return (
    <div className={`stat${tone ? ` stat-${tone}` : ''}`}>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

export default TestPanel;
